import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { classNames } from '../utils/classNames';
import { TOOLBAR_BUTTON } from '../button/toolbarButtonStyle';
import { filterOptions } from '../selector/TitleSelect';

// A toolbar button that opens a long checklist: the choices in sections, each with an optional second line, a search
// field over a long list, a limit on the ticked count, and a line at the bottom. The caller owns the selection:
// onToggle receives the id that the user clicked.
//
// - A choice can name its `section`: a gray heading shows over the first choice of each section.
// - A `disabled` choice cannot be ticked; its `note` follows its second line ("Taormina · No reservations").
// - With `max` reached, the choices that are not ticked are off; with `min`, the last ticked choices cannot be unticked.
// - With `search`, a list of `minOptions` choices or more gets a search field at its top. The field gets the focus when
//   the list opens. The search has the rule of TitleSelect: no case, no accents, each word of the query in the
//   `searchText` of the choice (default: its title).
// - At each open, the ticked choices come first in their section, so the selection shows without a scroll. The order
//   does not change while the list is open: a tick never moves a row under the pointer.
//
// - A long list ends at the bottom of a whole row: no row is cut at the bottom edge, and the list never ends with a heading.
//
// The look of the panel and of its rows is the look of the TitleSelect list. The Component knows nothing about hotels.

export type ChecklistMenuOption = {
	id: string,
	label: string,
	// A second line under the title, for example the city.
	detail?: string,
	// The heading of the group of the choice, for example "Property groups".
	section?: string,
	// The text that the search reads. Default: the title.
	searchText?: string,
	disabled?: boolean,
	// Why the choice is off, after the second line.
	note?: string,
};

export type ChecklistMenuSearch = {
	placeholder: string,
	// The text when no choice matches the query.
	empty: string,
	// The search shows from this count of choices. Default 8.
	minOptions?: number,
};

type Props = {
	// The content of the button, for example "Compare · 4 of 6".
	label: React.ReactNode,
	options: ChecklistMenuOption[],
	selected: string[],
	onToggle: (id: string) => void,
	// The name of the list for a screen reader.
	listLabel: string,
	max?: number,
	// Default 0: no ticked choice is a valid choice.
	min?: number,
	search?: ChecklistMenuSearch,
	// The line at the bottom of the list, for example "4 of 6 selected · up to 6".
	hint?: string,
	anchor?: 'bottom start' | 'bottom end' | 'bottom',
};

const PANEL = 'z-50 flex w-80 max-w-full flex-col rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none sm:w-96';
const SECTION_HEADING = 'px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500';

// The order at the open: the ticked choices first in each section, else the order of the caller.
const openOrder = (options: ChecklistMenuOption[], selected: string[]): ChecklistMenuOption[] => {
	const sections: string[] = [];
	options.forEach(option => {
		const section = option.section ?? '';
		if (!sections.includes(section)) sections.push(section);
	});
	return sections.flatMap(section => {
		const inSection = options.filter(option => (option.section ?? '') === section);
		return [...inSection.filter(option => selected.includes(option.id)), ...inSection.filter(option => !selected.includes(option.id))];
	});
};

// The list of up to MAX_LIST px ends at the bottom of its last whole row. A scroll box draws its content under its own
// padding, so the list ends at the row itself: the next row starts at the bottom edge and does not show. A heading with
// no row under it moves to the next scroll.
const MAX_LIST = 320;
const fitRows = (box: HTMLElement) => {
	box.style.removeProperty('max-height');
	if (box.scrollHeight <= MAX_LIST) return;
	const lines = Array.from(box.children as HTMLCollectionOf<HTMLElement>);
	const whole = lines.filter(line => line.offsetTop + line.offsetHeight <= MAX_LIST);
	let last = whole[whole.length - 1];
	if (last?.dataset.heading !== undefined) last = whole[whole.length - 2];
	if (last !== undefined) box.style.maxHeight = `${last.offsetTop + last.offsetHeight}px`;
};

const List = ({ options, selected, onToggle, listLabel, max, min, search, hint }: Omit<Props, 'label' | 'anchor'> & { min: number }) => {
	const [query, setQuery] = useState('');
	// The order of the open: a tick never moves a row. The list mounts at each open.
	const [ordered] = useState(() => openOrder(options, selected));
	const withSearch = search !== undefined && options.length >= (search.minOptions ?? 8);
	const shown = useMemo(() => {
		if (!withSearch) return ordered;
		const ids = new Set(filterOptions(ordered, query).map(option => option.id));
		return ordered.filter(option => ids.has(option.id));
	}, [ordered, query, withSearch]);
	const box = useRef<HTMLDivElement>(null);
	// The panel gets its place (the anchor) after the first layout, so the fit runs again in the next frame.
	useLayoutEffect(() => {
		const list = box.current;
		if (list === null) return;
		list.scrollTop = 0;
		fitRows(list);
		const frame = requestAnimationFrame(() => fitRows(list));
		return () => cancelAnimationFrame(frame);
	}, [shown]);

	return <>
		{withSearch && <div className="border-b border-gray-100 p-2">
			<div className="grid w-full grid-cols-1">
				{/* autoFocus: the list opens on a click of the button, and the search is the next step. */}
				<input type="search" autoFocus autoComplete="off" aria-label={search.placeholder} placeholder={search.placeholder}
					value={query} onChange={event => setQuery(event.target.value)}
					className="input col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 sm:text-sm/6" />
				<MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 ml-3 h-5 w-5 self-center text-gray-400" />
			</div>
		</div>}
		<div ref={box} role="group" aria-label={listLabel} className="relative max-h-80 overflow-y-auto p-2">
			{shown.map((option, index) => {
				const checked = selected.includes(option.id);
				const off = option.disabled === true
					|| (!checked && max !== undefined && selected.length >= max)
					|| (checked && selected.length <= min);
				const section = option.section;
				const heading = section !== undefined && section !== shown[index - 1]?.section ? section : undefined;
				const second = [option.detail, option.disabled === true ? option.note : undefined].filter((text): text is string => text !== undefined && text !== '');
				return <React.Fragment key={option.id}>
					{heading !== undefined && <div data-heading="" className={classNames(SECTION_HEADING, index === 0 ? 'pt-1' : 'pt-3')}>{heading}</div>}
					<label className={classNames('flex items-start gap-x-3 rounded-md px-3 py-1.5',
						checked ? 'bg-blue-50' : (off ? '' : 'hover:bg-gray-50'), off ? 'cursor-not-allowed' : 'cursor-pointer')}>
						<input type="checkbox" checked={checked} disabled={off} onChange={() => onToggle(option.id)}
							className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
						<span className="min-w-0">
							<span className={classNames('block truncate text-sm font-medium', off && !checked ? 'text-gray-400' : 'text-gray-900')}>{option.label}</span>
							{second.length > 0 && <span className="block truncate text-xs text-gray-500">{second.join(' · ')}</span>}
						</span>
					</label>
				</React.Fragment>;
			})}
			{shown.length === 0 && withSearch && <p className="px-3 py-1.5 text-sm text-gray-500">{search.empty}</p>}
		</div>
		{hint !== undefined && hint !== '' && <p className="border-t border-gray-100 px-3 pb-2 pt-1.5 text-xs text-gray-500">{hint}</p>}
	</>;
};

const ChecklistMenu: React.FunctionComponent<Props> = ({ label, anchor = 'bottom start', min = 0, ...list }) =>
	<Popover className="relative inline-block">
		<PopoverButton className={TOOLBAR_BUTTON}>
			{label}
			<ChevronDownIcon className="-mr-1 h-4 w-4 text-gray-400" aria-hidden="true" />
		</PopoverButton>
		<PopoverPanel anchor={{ to: anchor, gap: 8 }} className={PANEL}>
			<List {...list} min={min} />
		</PopoverPanel>
	</Popover>;

export default ChecklistMenu;
